/**
 * Attribution Manager (#1467)
 *
 * Captures WHERE a visitor came from, once, at landing — and keeps it so the
 * value is still available when they submit a form several pages later.
 *
 * Why this exists at all: the ad parameters only ever appear in the URL of the
 * LANDING page. By the time someone opens a car page and fills the enquiry
 * form, `window.location.href` is clean. Measured on Andys, only 27 of 109
 * ad-attributable leads still had the parameters in the submitted `sourceUrl`
 * — so parsing the form's own URL server-side loses three quarters of the
 * attribution. It has to be read at landing and persisted, exactly like
 * `sg_visitorId` in `sessionManager.js`.
 *
 * FIRST TOUCH WINS, with one exception: a stored touch that carries no click id
 * and no utm (a direct or organic arrival) is upgraded when a real campaign
 * touch shows up later. Otherwise someone who browses once directly and comes
 * back through an ad would be credited to "direct" forever. The server mirrors
 * this rule in `CrmLeadsUpsertService` — the two halves must agree, or a lead
 * deduped across visits gets whichever answer the server happens to hold.
 *
 * Privacy: only the landing PATH is kept, and the referrer is reduced to
 * origin + path, so no ad payload and no third-party query string (which in
 * practice carries email addresses from mail-tracking links) is retained.
 *
 * `deriveChannel` is duplicated in the backfill migration (#1467 part 2),
 * which cannot import across the pnpm-workspace boundary and must stay a
 * frozen snapshot anyway. The two MUST be changed together: a divergence does
 * not fail anything, it silently splits a channel-mix chart at the deploy date.
 */

const STORAGE_KEY = 'sg_attribution';

/** Paid-click identifiers, by the network that issues them. */
const CLICK_IDS = {
  google_ads: ['gclid', 'gbraid', 'wbraid'],
  meta: ['fbclid'],
  microsoft: ['msclkid'],
  tiktok: ['ttclid'],
};

const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

/** Bound every stored value — these reach a jsonb column on the lead. */
const MAX_VALUE_LENGTH = 200;

/**
 * Trim before capping. The API and the backfill both store the trimmed form;
 * if this one did not, `?utm_campaign=Spring%20Sale%20` would group as two
 * separate campaigns in the export the CSV columns exist to support.
 */
const clip = (v) => {
  if (typeof v !== 'string') return undefined;
  const trimmed = v.trim();
  return trimmed.length > 0 ? trimmed.slice(0, MAX_VALUE_LENGTH) : undefined;
};

/**
 * Mediums that prove the click was paid. Without one of these we will not
 * upgrade a tagged visit to an ad channel: a dealer tagging their free Google
 * Business Profile link `?utm_source=google&utm_medium=organic` is standard
 * practice, and counting it as Google Ads inflates exactly the number
 * marketing allocates spend on.
 */
const PAID_MEDIUMS = [
  'cpc',
  'ppc',
  'paid',
  'paidsearch',
  'paid_search',
  'paidsocial',
  'paid_social',
  'display',
  'cpm',
  'retargeting',
];

/** Exact-ish source matching — a substring test classifies `metadata` as Meta. */
const GOOGLE_SOURCE = /^google([_-]?ads?)?$/;
const META_SOURCE = /^(facebook|instagram|meta|fb|ig)([_-]?ads?)?$/;

/** Google *search* hosts only. `mail.google.com` is a referral, not organic. */
const GOOGLE_SEARCH_HOST = /^(www\.)?google\.[a-z]{2,}(\.[a-z]{2,})?$/;
const META_HOST = /(^|\.)(facebook|instagram|fb)\.(com|me)$/;

/** Hostname of a referrer, or '' when it will not parse. */
function hostOf(referrer) {
  try {
    return new URL(referrer).hostname.toLowerCase();
  } catch {
    return '';
  }
}

/**
 * Classify the touch into a channel the CRM and dashboards can group by,
 * so neither has to re-derive it from raw parameters.
 *
 * Matching is on the referrer's HOSTNAME, never the whole URL: the previous
 * `/(^|\.)google\./` form missed apex `google.com` entirely while matching any
 * URL that merely contained the text, so Gmail click-throughs were filed as
 * organic search and a blog post at `/facebook-ads-tips` was billed to Meta.
 *
 * @returns {'google_ads'|'meta'|'microsoft'|'tiktok'|'organic'|'referral'|'direct'}
 */
function deriveChannel(clickIds, utm, referrer) {
  for (const [network, params] of Object.entries(CLICK_IDS)) {
    if (params.some((p) => clickIds[p])) return network;
  }

  const source = (utm.utm_source || '').toLowerCase();
  const medium = (utm.utm_medium || '').toLowerCase();

  if (source) {
    // Manual tagging without auto-tagging, or a network that issues no click
    // id — but only when the medium says the click was paid.
    if (PAID_MEDIUMS.includes(medium)) {
      if (GOOGLE_SOURCE.test(source)) return 'google_ads';
      if (META_SOURCE.test(source)) return 'meta';
    }
    if (GOOGLE_SOURCE.test(source) && medium === 'organic') return 'organic';
    return 'referral';
  }

  const host = hostOf(referrer);
  if (!host) return 'direct';
  if (GOOGLE_SEARCH_HOST.test(host)) return 'organic';
  if (META_HOST.test(host)) return 'meta';
  return 'referral';
}

/**
 * Referrer reduced to origin + path.
 *
 * The query string is dropped deliberately: mail-tracking links routinely
 * carry the recipient's address (`?e=jan.jansen%40gmail.com`), and this value
 * is persisted to localStorage, posted to a public endpoint and stored in the
 * lead's jsonb. Keeping it would put third-party PII in the CRM.
 */
function safeReferrer(raw) {
  if (typeof raw !== 'string' || raw.length === 0) return undefined;
  try {
    const u = new URL(raw);
    return clip(`${u.origin}${u.pathname}`);
  } catch {
    return undefined;
  }
}

/** Read the current page's attribution signals. Pure — no storage access. */
function readTouch() {
  if (typeof window === 'undefined') return null;

  let params;
  try {
    params = new URLSearchParams(window.location.search);
  } catch {
    return null;
  }

  const clickIds = {};
  for (const list of Object.values(CLICK_IDS)) {
    for (const p of list) {
      const v = clip(params.get(p));
      if (v) clickIds[p] = v;
    }
  }

  const utm = {};
  for (const p of UTM_PARAMS) {
    const v = clip(params.get(p));
    if (v) utm[p] = v;
  }

  const referrer = safeReferrer(document.referrer);

  return {
    clickIds,
    utm,
    referrer,
    // Path only — deliberately NOT the query string.
    landingPath: clip(window.location.pathname),
    channel: deriveChannel(clickIds, utm, referrer),
    capturedAt: new Date().toISOString(),
  };
}

/** A touch is "campaign-bearing" if it names a source we could act on. */
function isCampaignTouch(touch) {
  if (!touch) return false;
  // Null-safe on purpose: `touch` can come straight out of localStorage, where
  // anything on the dealer's origin may have written a valid-JSON value of the
  // wrong shape. A throw here used to land in a bare catch that returned
  // BEFORE the rewrite, so the bad value was never repaired and attribution
  // capture became a permanent no-op for that browser.
  const clickIds = touch.clickIds;
  const utm = touch.utm;
  const hasClickId = !!clickIds && typeof clickIds === 'object' && Object.keys(clickIds).length > 0;
  const hasUtm = !!utm && typeof utm === 'object' && Object.keys(utm).length > 0;
  return hasClickId || hasUtm;
}

/** True for the record shape this module writes. Anything else is discarded. */
function isStoredRecord(value) {
  return (
    !!value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    !!value.clickIds &&
    typeof value.clickIds === 'object' &&
    !Array.isArray(value.clickIds) &&
    !!value.utm &&
    typeof value.utm === 'object' &&
    !Array.isArray(value.utm)
  );
}

/**
 * The stored record, or null.
 *
 * A value that parses but is not our shape is treated as absent AND removed,
 * so the next capture writes a clean record instead of tripping over it
 * forever.
 */
function readStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (isStoredRecord(parsed)) return parsed;
    localStorage.removeItem(STORAGE_KEY);
    return null;
  } catch {
    // Private mode, blocked storage, or corrupt JSON — attribution is a
    // nice-to-have and must never break lead capture.
    return null;
  }
}

/**
 * Capture this page's attribution. Call once per page load, at bootstrap.
 *
 * @returns {Object|null} the stored first-touch record
 */
export function captureAttribution() {
  const touch = readTouch();
  if (!touch) return null;

  try {
    const stored = readStored();

    // Upgrade a direct/organic first touch when a real campaign arrives.
    const useTouch = !stored || (!isCampaignTouch(stored) && isCampaignTouch(touch));
    const next = { ...(useTouch ? touch : stored) };

    // Last touch is only interesting when it differs from what we kept.
    if (!useTouch && isCampaignTouch(touch) && touch.channel !== next.channel) {
      next.lastTouchChannel = touch.channel;
    }

    // Skip the write when nothing changed — this runs on every page load of
    // every page of every client site, and a no-op setItem still wakes the
    // `storage` listeners of every other tab on the origin.
    const serialized = JSON.stringify(next);
    if (!stored || serialized !== JSON.stringify(stored)) {
      localStorage.setItem(STORAGE_KEY, serialized);
    }
    return next;
  } catch {
    return touch;
  }
}

/**
 * The stored first-touch record, flattened for the lead-capture payload.
 * Returns undefined when there is nothing worth sending, so the field is
 * simply absent from the request rather than an empty object.
 */
export function getAttribution() {
  const stored = readStored();
  if (!stored) return undefined;

  const { clickIds = {}, utm = {}, referrer, landingPath, channel, capturedAt, lastTouchChannel } =
    stored;

  // Built key by key rather than with shorthand: `{ channel, capturedAt }`
  // creates both keys even when both values are undefined, which made the
  // emptiness check below unreachable and shipped `{}` on every submit.
  const out = {};
  if (channel) out.channel = channel;
  if (capturedAt) out.capturedAt = capturedAt;
  Object.assign(out, clickIds, utm);
  if (referrer) out.referrer = referrer;
  if (landingPath) out.landingPath = landingPath;
  if (lastTouchChannel && lastTouchChannel !== channel) out.lastTouchChannel = lastTouchChannel;

  return Object.keys(out).length > 0 ? out : undefined;
}

/** Test seam. */
export function clearAttribution() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* no-op */
  }
}

export default { captureAttribution, getAttribution, clearAttribution };
